import { StatusColor } from 'src/app/standalones/components/status-indicator/status-indicator.component';
import { AnalyticsUserStat, IAnalyticsInsight, SessionStatus } from '../models/analytics';
import { SpaceRecording } from '../models/space-recording';

/**
 * Converts Date object to string format, ex. Dec 15th 2022, 17:55
 * @param date
 * @returns string version of date
 */
export function getDateString(date: Date | string): string {
  date = new Date(date);
  const month = date.toLocaleString('default', { month: 'short' });
  const day = date.getDate() + getDaySuffix(date.getDate());
  const year = date.getFullYear();
  return `${month} ${day} ${year}, ${getTimeString(date)}`;
}
/** Applies fresh API recording data while keeping list-only display fields. */
export function mergeFreshRecordingWithListContext(
  listRecording: SpaceRecording,
  freshRecording: SpaceRecording | null,
): SpaceRecording {
  return {
    ...listRecording,
    ...freshRecording,
    formattedStartTime:
      listRecording.formattedStartTime ?? getDateString(freshRecording?.startingTime ?? ''),
    summary: freshRecording?.summary ?? null,
  };
}
/**
 * Converts duration between two date objects to a string format, ex 2 hrs 15 min
 * @param startTime
 * @param endTime
 * @returns string version of duration
 */
function getSessionTime(sessionLength: number | undefined): string {
  if (sessionLength) {
    return convertMinutesDurationToString(sessionLength);
  }
  return 'N/A';
}

function getSessionLength(
  startTime: Date | string,
  endTime: Date | string | null,
): number | undefined {
  if (!endTime) {
    return undefined;
  }
  startTime = new Date(startTime);
  endTime = new Date(endTime);
  return Math.max((endTime.getTime() - startTime.getTime()) / 60000, 1);
}

/**
 * Converts callStats array to duration in minutes
 * @param callStats
 * @returns duration in minutes
 */
function getCallTime(callStats: IAnalyticsInsight['callStats']): number {
  let durationInMinutes = 0;
  callStats.forEach((call) => {
    durationInMinutes +=
      (new Date(call.callEndTime).getTime() - new Date(call.callStartTime).getTime()) / 60000;
  });
  return durationInMinutes;
}

export function getTimeString(date: Date | string): string {
  const dateObj = new Date(date);
  let hours = dateObj.getHours();
  const TT = hours >= 12 ? 'pm' : 'am';
  hours = hours > 12 ? hours - 12 : hours;
  const minutes = dateObj.getMinutes();
  const minutesPadded = minutes >= 10 ? minutes : `0${minutes}`;
  return `${hours}:${minutesPadded}${TT}`;
}

/**
 * Returns suffix of day
 * @param n day
 * @returns
 */
export function getDaySuffix(n: number): string {
  return ['st', 'nd', 'rd'][((((n + 90) % 100) - 10) % 10) - 1] || 'th';
}

/**
 *
 * @param durationInMinutes
 * @returns string version of duration
 */
export function convertMinutesDurationToString(durationInMinutes: number): string {
  if (durationInMinutes === undefined) {
    return 'N/A';
  }
  const hours = Math.floor(durationInMinutes / 60);
  const minutes = Math.floor(durationInMinutes % 60);
  const minutesSuffix = minutes <= 1 ? 'min' : 'mins';
  if (hours > 0) {
    const hoursSuffix = hours > 1 ? 'hrs' : 'hr';
    return `${hours} ${hoursSuffix} ${minutes} ${minutesSuffix}`;
  } else if (minutes >= 1) {
    return `${minutes} ${minutesSuffix}`;
  } else {
    return '<1 min';
  }
}

export function parseSessionTitle(sessionTitle: string) {
  if (new Date(sessionTitle).toUTCString() === sessionTitle) {
    return getDateString(sessionTitle);
  }
  return sessionTitle;
}

export function getMaxCallTime(attendees: Array<AnalyticsUserStat>) {
  let max = 0;
  attendees.forEach((attendee) => {
    const duration = attendee.callDuration ?? 0;
    if (duration > max) {
      max = duration;
    }
  });
  return max / 60;
}

export function hasCall(session: IAnalyticsInsight) {
  if (session.callStats.length > 0) {
    return true;
  }
  for (const attendee of session.attendees) {
    if ((attendee.callDuration ?? 0) > 0) {
      return true;
    }
  }
  return false;
}

export interface IAnalyticsInsightParsed extends IAnalyticsInsight {
  parsedCallLength: string;
  sessionLength?: number;
  numberOfAttendees: number;
  parsedSessionLength: string;
  parsedSessionStartTime: string;
  parsedCurrentSessionStatus: SessionStatus;
  parsedSessionTitle: string;
  callLength: number;
  /** Display string for the "billed" header: V1 duration or V2 dollar amount. */
  parsedBilledValue: string;
}

/**
 * Formats analytics session fields for UI.
 * When enableV2Billing is true, "billed" shows pinned stepPricingRating.totalCredits as dollars
 * (despite the *Credits name). Otherwise uses V1 billable minutes (+ optional host discount).
 */
export function formatAndAugmentSessionFields(
  session: IAnalyticsInsight,
  subtractHostDuration: boolean,
  enableV2Billing = false,
): IAnalyticsInsightParsed {
  const callLength = getCallLength(session);
  const sessionLength = getSessionLength(session.sessionStartTime, session.sessionEndTime);
  const billableTimeLength =
    (session.billableSpaceTimeMinutes || 0) -
    (subtractHostDuration ? session.billableHostDiscountSpaceTimeMinutes || 0 : 0);
  const attendees = setObserversNumbers(session.attendees);
  const roomAttendees = session.roomAttendees.map((room) => ({
    ...room,
    attendees: setObserversNumbers(room.attendees),
  }));

  const parsedBilledValue = enableV2Billing
    ? getBilledAmountString(session.stepPricingRating?.totalCredits ?? 0)
    : getBillableTimeString(billableTimeLength);

  return {
    ...session,
    attendees,
    roomAttendees,
    parsedCallLength: getCallLengthString(callLength),
    numberOfAttendees: attendees.length,
    parsedSessionStartTime: getDateString(new Date(session.sessionStartTime)),
    parsedCurrentSessionStatus: getParsedCurrentSessionStatus(session),
    parsedSessionTitle: parseSessionTitle(session.sessionTitle),
    callLength,
    sessionLength,
    parsedSessionLength: getSessionTime(sessionLength),
    parsedBilledValue,
  };
}

export function setObserversNumbers<
  T extends { isObserver?: boolean; currentObserverNumber?: number },
>(attendees: T[]): T[] {
  let observerCount = 0;

  return attendees.map((attendee) => ({
    ...attendee,
    currentObserverNumber: attendee.isObserver ? ++observerCount : undefined,
  }));
}

function getCallLength(session: IAnalyticsInsight): number {
  if (session.callStats.length > 0) {
    return Math.max(getCallTime(session.callStats), getMaxCallTime(session.attendees));
  } else {
    return getMaxCallTime(session.attendees);
  }
}

function getBillableTimeString(billableTimeLength: number) {
  return billableTimeLength === 0 ? '' : convertMinutesDurationToString(billableTimeLength);
}

/** Formats V2 session billed dollars for the insights header (e.g. "$1.40"). */
function getBilledAmountString(totalCreditsDollars: number) {
  return `$${Number(totalCreditsDollars || 0).toFixed(2)}`;
}

function getCallLengthString(callLength: number) {
  return callLength === 0 ? '' : convertMinutesDurationToString(callLength);
}

function getParsedCurrentSessionStatus(session: IAnalyticsInsight): SessionStatus {
  if (session.status === SessionStatus.PROCESSING) {
    return SessionStatus.PROCESSING;
  } else if (!!session.sessionEndTime === false) {
    return SessionStatus.IN_PROGRESS;
  } else {
    return SessionStatus.PROCESSED;
  }
}

export function isAnalyticsSessionLive(session: Pick<IAnalyticsInsight, 'status'>): boolean {
  return (
    session.status === SessionStatus.IN_PROGRESS || session.status === SessionStatus.PROCESSING
  );
}

export enum SpaceDataLevel {
  ROOM = 'ROOM',
  SPACE = 'SPACE',
}

export const DEFAULT_ERROR_STATE = '-';
export const NA = 'N/A';
export const MULTIPLE_VALUES = 'Multiple';

export function calculateDurationPercentage(
  duration: number | undefined,
  totalDuration: number,
): number | undefined {
  if (!totalDuration || duration === undefined) {
    return undefined;
  }
  return Math.round((duration / totalDuration) * 100);
}

export class QualityState {
  constructor(public level: QualityLevel, public percentage: number) {}

  get color(): StatusColor {
    switch (this.level) {
      case QualityLevel.GOOD:
        return StatusColor.SUCCESS;
      case QualityLevel.AVERAGE:
        return StatusColor.WARNING;
      case QualityLevel.POOR:
        return StatusColor.ERROR;
      default:
        return StatusColor.ERROR;
    }
  }
}

export enum QualityLevel {
  GOOD = 'Good',
  AVERAGE = 'Average',
  POOR = 'Poor',
}

export function calculateNetworkQuality(
  duration: number | undefined,
  totalDuration: number,
): QualityState | typeof NA {
  const percentage = calculateDurationPercentage(duration, totalDuration);
  if (percentage === undefined) {
    return NA;
  }

  if (percentage < 2.5) {
    return new QualityState(QualityLevel.GOOD, percentage);
  }

  if (percentage <= 20) {
    return new QualityState(QualityLevel.AVERAGE, percentage);
  }

  return new QualityState(QualityLevel.POOR, percentage);
}

export function calculateDevicePerformance(
  duration: number | undefined,
  totalDuration: number,
): QualityState | typeof NA {
  const percentage = calculateDurationPercentage(duration, totalDuration);
  if (percentage === undefined) {
    return NA;
  }

  if (percentage < 5) {
    return new QualityState(QualityLevel.GOOD, percentage);
  }

  if (percentage <= 30) {
    return new QualityState(QualityLevel.AVERAGE, percentage);
  }

  return new QualityState(QualityLevel.POOR, percentage);
}

export function evaluateDeviceErrors(
  micErrors: string[] | null | undefined,
  camErrors: string[] | null | undefined,
): string {
  const combinedErrors: string[] = [];

  if (micErrors?.length) {
    combinedErrors.push(...micErrors.map((err) => `mic:${err}`));
  }

  if (camErrors?.length) {
    combinedErrors.push(...camErrors.map((err) => `cam:${err}`));
  }

  return combinedErrors.length ? combinedErrors.join('\n') : DEFAULT_ERROR_STATE;
}

export function evaluateDeviceTypes(deviceTypes: string[] | null): string {
  if (!deviceTypes || deviceTypes.length === 0) {
    return NA;
  }

  return deviceTypes.length === 1 ? deviceTypes[0] : MULTIPLE_VALUES;
}

export function evaluateBrowsers(browsers: string[] | null): string {
  if (!browsers || browsers.length === 0) {
    return NA;
  }

  return browsers.length === 1 ? browsers[0] : MULTIPLE_VALUES;
}

export function getDeviceTypesRaw(deviceTypes: string[] | null): string {
  if (!deviceTypes || deviceTypes.length === 0) {
    return '';
  }
  return deviceTypes.join(', ');
}

export function getBrowsersRaw(browsers: string[] | null): string {
  if (!browsers || browsers.length === 0) {
    return '';
  }
  return browsers.join(', ');
}
